import { CameraAnimationReturn } from "@/data-preparation-tool/hooks/use-camera-animation";
import { calculateCameraDistanceToFrameSize } from "@/data-preparation-tool/utils/ortho-perspective-switch-utils";
import { PointCloudObject } from "@/object-cache";
import { Perspective } from "@/registration-tools/common/store/registration-datatypes";
import { SupportedCamera } from "@faro-lotv/lotv";
import { Box3, OrthographicCamera, PerspectiveCamera, Vector3 } from "three";

/** Position distance threshold at which an animation is played */
const ANIMATION_THRESHOLD = 0.1;

const BOX_DIAG_MULTIPLIER = 0.5;

/** The camera direction corresponding to the given perspective. */
export const PERSPECTIVE_DIRECTIONS: Record<Perspective, Vector3> =
  Object.freeze({
    [Perspective.topView]: new Vector3(0, -1, 0),
    [Perspective.frontView]: new Vector3(0, 0, -1),
    [Perspective.sideView]: new Vector3(-1, 0, 0),
  });

/**
 * @param camera the camera to check
 * @returns `true` if there is no camera or if it looks top-down, else false.
 */
export function isTopDownView(camera: SupportedCamera | undefined): boolean {
  if (!camera) return true;

  const precision = 0.001;

  return (
    camera
      .getWorldDirection(new Vector3())
      .distanceTo(PERSPECTIVE_DIRECTIONS[Perspective.topView]) < precision
  );
}

/**
 * Computes and updates the camera frustum to visualize a bounding box that contains all relevant objects
 *
 * @param camera The camera to compute the frustum and apply it to
 * @param bbox the bounding box that contains all the relevant objects
 */
function updateOrthoCameraFrustum(
  camera: OrthographicCamera,
  bbox: Box3,
): void {
  const diagonal = bbox.max.distanceTo(bbox.min);
  if (camera instanceof OrthographicCamera) {
    const aspectRatio =
      (camera.right - camera.left) / (camera.top - camera.bottom);
    camera.top = diagonal * BOX_DIAG_MULTIPLIER;
    camera.bottom = -camera.top;
    camera.right = camera.top * aspectRatio;
    camera.left = camera.bottom * aspectRatio;
    camera.near = 0.01;
    camera.far = 4 * diagonal;
    camera.updateProjectionMatrix();
  }
}

/**
 * Create the bounding box for the point cloud that can be used in the computation
 * of the camera position
 *
 * @param pointCloud point cloud object for which bounding box needs to be computed
 * @param bbox The bounding box to write the result into. Cache this value to avoid reallocations.
 * @returns bounding box for the point cloud, same object as `bbox`.
 */
export function computePointCloudBoundingBox(
  pointCloud: PointCloudObject,
  bbox: Box3,
): Box3 {
  return bbox.copy(pointCloud.boundingBox).applyMatrix4(pointCloud.matrixWorld);
}

/**
 * Computes a bounding box that contains both point clouds.
 *
 * @param pointClouds The pointclouds
 * @param bboxUnion The box to write the result into. Cache this value to avoid reallocations.
 * @returns bounding box that contains both pointclouds
 */
export function computeCombinedPointCloudBoundingBox(
  pointClouds: PointCloudObject[],
  bboxUnion: Box3,
): Box3 {
  // Temporary value to reuse for the bounding box of each point cloud
  const tempBbox = new Box3();

  for (const pointCloud of pointClouds) {
    bboxUnion.union(computePointCloudBoundingBox(pointCloud, tempBbox));
  }

  return bboxUnion;
}

/**
 * @param pointClouds the point clouds to compute the center of
 * @returns the center point of all point clouds
 */
export function computeCombinedPointCloudCenter(
  pointClouds: PointCloudObject[],
): Vector3 {
  const combinedBbox = computeCombinedPointCloudBoundingBox(
    pointClouds,
    new Box3(),
  );
  return combinedBbox.getCenter(new Vector3());
}

type CenterCameraOnBoundingBoxProps = {
  /** The camera to center on the bounding box. */
  camera: SupportedCamera;

  /** The bounding box to frame the camera for. */
  bbox: Box3;

  /**
   * The new direction the camera should face.
   *
   * Defaults to the camera's current direction.
   */
  camDirection?: Vector3;

  /** Function used to animate the camera to its new position. */
  startAnimation?: CameraAnimationReturn["startAnimation"];

  /** Callback to call once the animation finished, or immediately when there is no animation. */
  onAnimationFinished?(target: Vector3): void;
};

/** Center the orthographic camera on the given bounding box. */
export function centerOrthoCameraOnBoundingBox({
  camera,
  bbox,
  camDirection,
  onAnimationFinished,
}: CenterCameraOnBoundingBoxProps & { camera: OrthographicCamera }): void {
  const target = bbox.getCenter(new Vector3());
  const diagonal = bbox.max.distanceTo(bbox.min);
  updateOrthoCameraFrustum(camera, bbox);

  const revDirection =
    camDirection?.clone() ?? camera.getWorldDirection(new Vector3());
  revDirection.normalize().multiplyScalar(-1);

  camera.position.copy(target).addScaledVector(revDirection, diagonal);

  camera.lookAt(target);
  camera.updateProjectionMatrix();

  // Animations are not supported for now
  if (onAnimationFinished) {
    onAnimationFinished(target);
  }
}

/** Center the perspective camera on the given bounding box. */
function centerPerspectiveCameraOnBoundingBox({
  camera,
  bbox,
  camDirection,
  startAnimation,
  onAnimationFinished: customAnimationFinished,
}: CenterCameraOnBoundingBoxProps & { camera: PerspectiveCamera }): void {
  const target = bbox.getCenter(new Vector3());
  const diagonal = bbox.max.distanceTo(bbox.min);

  // Vector from the target to the intended camera position
  const directionInv =
    camDirection?.clone() ?? camera.getWorldDirection(new Vector3());
  directionInv.normalize().multiplyScalar(-1);

  const cameraPosition = target
    .clone()
    .add(
      directionInv.multiplyScalar(
        calculateCameraDistanceToFrameSize(camera.getEffectiveFOV(), diagonal),
      ),
    );

  function onAnimationFinished(): void {
    camera.position.copy(cameraPosition);
    camera.lookAt(target);
    camera.updateProjectionMatrix();

    if (customAnimationFinished) {
      customAnimationFinished(target);
    }
  }

  if (
    startAnimation &&
    camera.position.distanceTo(cameraPosition) > ANIMATION_THRESHOLD
  ) {
    startAnimation(
      camera,
      {
        position: cameraPosition,
        quaternion: camera.quaternion,
      },
      () => {
        camera.position.copy(cameraPosition);
        camera.lookAt(target);
        camera.updateProjectionMatrix();
        onAnimationFinished();
      },
    );
  } else {
    // No animation, call the callback immediately
    onAnimationFinished();
  }
}

/** Center the camera on the given bounding box. */
export function centerCameraOnBoundingBox({
  camera,
  ...rest
}: CenterCameraOnBoundingBoxProps): void {
  if (camera instanceof OrthographicCamera) {
    centerOrthoCameraOnBoundingBox({ camera, ...rest });
  } else {
    centerPerspectiveCameraOnBoundingBox({ camera, ...rest });
  }
}

/**
 * Centers the camera on the provided point clouds.
 *
 * @param pointclouds The pointclouds to center the camera on
 * @param camera the orthographic camera to be centered
 * @param perspective The view type: top, front, or side.
 */
export function centerCameraOnPointClouds(
  pointclouds: PointCloudObject[],
  camera: OrthographicCamera,
  perspective?: Perspective,
): void {
  const combinedBbox = computeCombinedPointCloudBoundingBox(
    pointclouds,
    new Box3(),
  );
  centerOrthoCameraOnBoundingBox({
    camera,
    bbox: combinedBbox,
    camDirection: PERSPECTIVE_DIRECTIONS[perspective ?? Perspective.topView],
  });
}
